import { isNonEmpty } from '@sgme/fp';
import type { Action } from 'state/actions';
import { WORKSPACE_TAB_REMOVED } from 'state/common-form/commonForm.actionTypes';
import { QuoteState } from 'state/quotes/infos/infos.model';
import { addKey, addOrPatchKey, addOrReplaceKey, patchKey, removeKey } from 'state/utils/collection';
import { addRejectionError, removeRejectionError } from 'state/utils/rejectionError';
import { dateNow } from 'utils/core/now';

import * as actionTypes from '../dynamicForm.actionTypes';
import type { DynamicFormInfosStateMap, DynamicInfos } from './infos.model';

export function infosReducer(state: DynamicFormInfosStateMap = {}, action: Action): DynamicFormInfosStateMap {
  switch (action.type) {
    case 'WORKSPACE_RESET_TAB_REQUESTED':
      return removeKey(state, action.tabId);
    case 'DEFAULT_FORM_RECEIVED': {
      if (action.workspace !== 'dynamic') {
        return state;
      }
      const {
        tabId,
        instrumentName,
        instrumentContractName,
        version,
        negotiationId,
        actions,
        quoteQuery,
        remunerationMode,
        validationErrors,
        etag,
        sendWholeFormData,
      } = action;
      const infos: DynamicInfos = {
        actions,
        negotiationId,
        instrumentName,
        instrumentContractName,
        quoteQuery,
        version,
        remunerationMode,
        validationErrors: isNonEmpty(validationErrors) ? validationErrors : undefined,
        etag,
        sendWholeFormData,
      };
      return addKey(state, tabId, infos);
    }

    case 'STREAMING_QUOTE_CLOSED':
    case 'STREAMING_QUOTE_EXPIRED':
      return action.workspace === 'dynamic'
        ? patchKey(state, action.tabId, () => ({
            documentation: undefined,
            quoteState: 'Previous' as QuoteState,
          }))
        : state;
    case 'FORM_ENRICHMENT_RECEIVED': {
      if (action.workspace !== 'dynamic') {
        return state;
      }
      const {
        tabId,
        instrumentName,
        instrumentContractName,
        version,
        actions,
        quoteQuery,
        validationErrors,
        remunerationMode,
      } = action;
      return patchKey(state, tabId, () => ({
        instrumentName,
        instrumentContractName,
        version,
        actions,
        quoteQuery,
        validationErrors: isNonEmpty(validationErrors) ? validationErrors : undefined,
        remunerationMode,
        technicalWarning: undefined,
      }));
    }
    case 'QUOTE_ACTIVE_FORM_TAB_CHANGED':
      if (action.workspace !== 'dynamic') {
        return state;
      }
      return patchKey(state, action.tabId, () => ({
        formActiveTab: action.formTabId,
      }));
    case 'FORM_ENRICHMENT_REQUESTED':
      if (action.workspace !== 'dynamic') {
        return state;
      }
      return patchKey(state, action.tabId, () => ({ enrichmentError: undefined }));
    case 'STREAMING_QUOTE_ACKED':
      if (action.workspace !== 'dynamic') {
        return state;
      }
      return patchKey(state, action.tabId, () => ({
        quoteId: action.quoteId,
        quoteState: 'Live' as QuoteState,
      }));
    case 'QUOTE_ACKED':
      if (action.workspace !== 'dynamic') {
        return state;
      }
      return patchKey(state, action.tabId, () => ({
        quoteId: action.quoteId,
        quoteState: 'Live' as QuoteState,
        version: action.newVersion,
      }));
    case actionTypes.DYNAMIC_BOOKING_REQUESTED:
    case 'QUOTE_TRADE_REJECTED':
      if (action.type === 'QUOTE_TRADE_REJECTED' && action.workspace !== 'dynamic') {
        return state;
      }
      return addOrPatchKey(state, action.tabId, () => ({
        quoteId: action.quoteId,
        quoteState: 'Live' as QuoteState,
      }));
    case actionTypes.DYNAMIC_BOOKING_ACKED:
      return patchKey(state, action.tabId, () => ({
        quoteState: 'Live' as QuoteState,
      }));

    case 'UPDATE_RFQ_VERSION':
      if (action.workspace !== 'dynamic') {
        return state;
      }
      return patchKey(state, action.tabId, () => ({
        version: action.newVersion,
      }));
    case 'FORM_ENRICHMENT_ERROR_DISCARDED':
      if (action.workspace !== 'dynamic') {
        return state;
      }
      return patchKey(state, action.tabId, () => {
        return {
          enrichmentError: undefined,
          crossBrowserDirty: undefined,
        };
      });
    case 'FORM_TAB_ROLLBACKED':
      if (action.workspace !== 'dynamic') {
        return state;
      }
      return patchKey(state, action.tabId, () => {
        return {
          enrichmentError: undefined,
          crossBrowserDirty: undefined,
        };
      });
    case 'FORM_ENRICHMENT_FAILED':
      if (action.workspace !== 'dynamic') {
        return state;
      }
      return patchKey(state, action.tabId, () => ({ enrichmentError: action.error }));
    case 'QUOTE_REMOVED':
      if (action.workspace !== 'dynamic') {
        return state;
      }
      return patchKey(state, action.tabId, infos => {
        if (action.quoteId !== infos.quoteId) {
          return infos; // message for another quote
        }
        return {
          quoteId: undefined,
          quoteState: undefined,
        };
      });
    case 'PREVIOUS_QUOTE_LOAD_REQUESTED':
      if (action.workspace !== 'dynamic') {
        return state;
      }
      return addOrPatchKey(state, action.tabId, () => ({
        quoteId: action.quoteId,
        quoteState: 'Previous' as QuoteState,
      }));

    case 'PREVIOUS_QUOTE_LOADING_FAILED':
      if (action.workspace !== 'dynamic') {
        return state;
      }
      return addOrPatchKey(state, action.tabId, () => ({
        quoteId: undefined,
        quoteState: undefined,
      }));

    case 'QUOTE_STALE':
      if (action.workspace !== 'dynamic') {
        return state;
      }
      return patchKey(state, action.tabId, () => ({
        quoteState: action.hasFormChanged ? ('Inconsistent' as QuoteState) : ('Previous' as QuoteState),
      }));

    case 'TRADE_AUTH_RECEIVED': {
      if (action.workspace !== 'dynamic') {
        return state;
      }
      const tradeAuthCreationDate = action.when.getTime();
      const now = dateNow();
      const isPastQuote = tradeAuthCreationDate + action.expirationDelayInSeconds * 1000 < now;
      const tradeAuthGranted = action.status === 'Granted';

      if (!tradeAuthGranted) {
        return addOrPatchKey(state, action.tabId, () => ({
          quoteId: action.quoteId,
          quoteState: 'Live' as QuoteState,
        }));
      }
      return addOrPatchKey(state, action.tabId, () => ({
        quoteId: action.quoteId,
        quoteState: isPastQuote ? 'Previous' : ('Live' as QuoteState),
      }));
    }

    case 'FORM_RELOADED': {
      if (action.workspace !== 'dynamic') {
        return state;
      }
      return addOrReplaceKey<DynamicInfos>(state, action.tabId, action);
    }
    case WORKSPACE_TAB_REMOVED:
      return removeKey(state, action.id);
    case 'DYNAMIC_SESSION_MODIFIED':
      return patchKey(state, action.tabId, () => ({
        crossBrowserDirty: true,
      }));
    case 'QUOTE_TRADE_REQUEST_REJECTED':
      if (action.workspace !== 'dynamic') {
        return state;
      }
      return patchKey(state, action.tabId, previousState => ({
        validationErrors: addRejectionError(previousState.validationErrors ?? [], action.error),
      }));
    case 'QUOTE_TRADE_REQUESTED':
      if (action.workspace !== 'dynamic') {
        return state;
      }
      return patchKey(state, action.tabId, previousState => ({
        validationErrors: removeRejectionError(previousState.validationErrors ?? []),
      }));
    case 'STREAMING_TRADE_REQUESTED':
      if (action.workspace !== 'dynamic') {
        return state;
      }
      return patchKey(state, action.tabId, previousState => ({
        validationErrors: removeRejectionError(previousState.validationErrors ?? []),
      }));
    case 'FORM_DOCUMENTATION_V3_RECEIVED':
      if (action.workspace !== 'dynamic') {
        return state;
      }
      const { tabId, documentation } = action;
      return patchKey(state, tabId, info => ({
        ...info,
        documentation: {
          docs: documentation.docs,
        },
      }));
    default:
      return state;
  }
}
